import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import {
  Sparkles, Music, Video, Star, TrendingUp, Mic, Radio,
  Headphones, Building2, Users, FileText, Clock, DollarSign,
  Loader2, ChevronRight, CheckCircle, Search, ArrowLeft, Zap
} from 'lucide-react';

const SLOT_ICONS = {
  top_song_week: Music,
  top_video_week: Video,
  featured_artist_week: Star,
  artist_on_rise_week: TrendingUp,
  top_podcaster_week: Mic,
  top_streamer_week: Radio,
  top_producer_week: Headphones,
  top_studio_week: Building2,
  community_spotlight_week: Users,
  top_story_week: FileText,
  spotlight_interview_week: Mic,
  soundstage_spotlight_paid: Sparkles,
};

const SLOT_DESCRIPTIONS = {
  top_song_week: 'Feature your track as the #1 song on the platform',
  top_video_week: 'Showcase your video as the top video of the week',
  featured_artist_week: 'Be highlighted as the featured artist of the week',
  artist_on_rise_week: 'Get promoted as a rising artist in the community',
  top_podcaster_week: 'Feature your podcast as the top show of the week',
  top_streamer_week: 'Get promoted as the top live streamer',
  top_producer_week: 'Showcase your production work to the community',
  top_studio_week: 'Promote your studio to artists looking to record',
  community_spotlight_week: 'Feature your organization in the community spotlight',
  top_story_week: 'Promote your story as the top post of the week',
  spotlight_interview_week: 'Feature your interview in the spotlight section',
  soundstage_spotlight_paid: 'Premium spotlight slot visible across the platform',
};

export default function PromoteContentPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, getAuthHeaders } = useAuth();
  const [step, setStep] = useState(1);
  const [pricing, setPricing] = useState([]);
  const [slots, setSlots] = useState({});
  const [myContent, setMyContent] = useState({ tracks: [], videos: [] });
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  // Selection state
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      toast.info('Sign in to promote your content');
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [pricingRes, slotsRes, tracksRes, videosRes] = await Promise.all([
        fetch(`${API}/promotions/pricing`),
        fetch(`${API}/featured-slots`),
        fetch(`${API}/tracks?user_id=${user.user_id}&limit=50`, { headers }),
        fetch(`${API}/videos?user_id=${user.user_id}&limit=50`, { headers }),
      ]);

      if (pricingRes.ok) {
        const data = await pricingRes.json();
        // Stats engine returns {slots: [...]} with slot_key, price_cents, duration_days
        setPricing(data.slots || data.pricing || []);
      }
      if (slotsRes.ok) {
        const data = await slotsRes.json();
        // Convert array to object keyed by slot_key
        const slotsObj = {};
        if (Array.isArray(data)) {
          data.forEach(s => { slotsObj[s.slot_key] = s; });
        } else if (Array.isArray(data.slots)) {
          data.slots.forEach(s => { slotsObj[s.slot_key] = s; });
        } else {
          Object.assign(slotsObj, data.slots || data);
        }
        setSlots(slotsObj);
      }
      if (tracksRes.ok) {
        const data = await tracksRes.json();
        setMyContent(prev => ({ ...prev, tracks: Array.isArray(data) ? data : (data.tracks || []) }));
      }
      if (videosRes.ok) {
        const data = await videosRes.json();
        setMyContent(prev => ({ ...prev, videos: Array.isArray(data) ? data : (data.videos || []) }));
      }
    } catch (err) {
      console.error('Failed to load promotion data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedSlot || !selectedContent) {
      toast.error('Please complete all selections');
      return;
    }

    setPurchasing(true);
    try {
      const res = await fetch(`${API}/promotions/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          slot_key: selectedSlot,
          target_id: selectedContent.id,
          target_type: selectedContent.type === 'profile' ? 'user' : selectedContent.type,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.checkout_url) {
          toast.success('Redirecting to Stripe checkout...');
          window.location.href = data.checkout_url;
          return;
        }
        toast.success(data.message || 'Promotion created!');
        navigate('/promotions');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create checkout');
      }
    } catch (err) {
      toast.error('Payment error. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="promote-content-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
        <div className="text-center mb-10">
          <Badge className="mb-3 bg-yellow-500/20 text-yellow-400">Paid Promotion</Badge>
          <h1 className="text-3xl sm:text-4xl font-black uppercase mb-2">
            Promote Your <span className="text-secondary">Content</span>
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Get your music, videos, or profile featured on the Soundstage homepage and charts.
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                  step === s ? 'bg-secondary text-black' : step > s ? 'bg-green-500 text-white' : 'bg-muted/50 text-muted-foreground'
                }`}
                data-testid={`step-indicator-${s}`}
              >
                {step > s ? <CheckCircle className="w-4 h-4" /> : s}
              </div>
              {s < 2 && <div className={`w-12 h-0.5 ${step > s ? 'bg-green-500' : 'bg-muted/50'}`} />}
            </div>
          ))}
        </div>
        <div className="flex justify-center gap-16 mb-8 text-xs text-muted-foreground">
          <span>Choose Slot</span>
          <span>Select Content & Pay</span>
        </div>

        {/* Step 1: Choose Slot */}
        {step === 1 && (
          <div className="space-y-4" data-testid="step-1-slots">
            <h2 className="text-xl font-bold mb-4">Select a Featured Slot</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {pricing.map(slot => {
                const key = slot.slot_key;
                const Icon = SLOT_ICONS[key] || Star;
                const currentSlotData = slots[key] || {};
                const isOccupied = currentSlotData.selected_id && currentSlotData.selection_mode === 'paid';
                const isSelected = selectedSlot === key;

                return (
                  <Card
                    key={key}
                    className={`p-4 cursor-pointer transition-all border-2 ${
                      isSelected
                        ? 'border-secondary bg-secondary/10'
                        : isOccupied
                        ? 'border-border opacity-50 cursor-not-allowed'
                        : 'border-border hover:border-border'
                    }`}
                    onClick={() => !isOccupied && setSelectedSlot(key)}
                    data-testid={`slot-option-${key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-secondary/20' : 'bg-muted/30'}`}>
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-secondary' : ''}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm">{slot.label || key.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {SLOT_DESCRIPTIONS[key] || 'Featured placement on the platform'}
                        </p>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{slot.duration_days} days</span>
                          <span className="font-bold text-secondary text-sm">{slot.price_display}</span>
                        </div>
                        {isOccupied && (
                          <Badge variant="outline" className="text-xs mt-1 text-yellow-400 border-yellow-400/30">
                            Currently Occupied (paid)
                          </Badge>
                        )}
                      </div>
                      {isSelected && <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />}
                    </div>
                  </Card>
                );
              })}
            </div>
            <div className="flex justify-end mt-6">
              <Button
                onClick={() => setStep(2)}
                disabled={!selectedSlot}
                className="bg-secondary hover:bg-secondary/90"
                data-testid="next-step-1"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Select Content & Pay */}
        {step === 2 && (
          <div className="space-y-4" data-testid="step-2-content">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Select Content & Checkout</h2>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            </div>

            {/* Selected Slot Summary */}
            {selectedSlot && (() => {
              const slotPricing = pricing.find(p => p.slot_key === selectedSlot);
              return slotPricing ? (
                <Card className="p-4 bg-secondary/5 border-secondary/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Selected Slot</p>
                      <p className="font-bold">{slotPricing.label}</p>
                      <p className="text-xs text-muted-foreground">{slotPricing.duration_days} days</p>
                    </div>
                    <p className="text-2xl font-black text-secondary">{slotPricing.price_display}</p>
                  </div>
                </Card>
              ) : null;
            })()}

            {/* Profile option */}
            <Card
              className={`p-4 cursor-pointer transition-all border-2 ${
                selectedContent?.type === 'profile' ? 'border-secondary bg-secondary/10' : 'border-border hover:border-border'
              }`}
              onClick={() => setSelectedContent({ type: 'profile', id: user.user_id, title: 'My Profile' })}
              data-testid="content-option-profile"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                  <Star className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <p className="font-bold text-sm">My Profile</p>
                  <p className="text-xs text-muted-foreground">Promote yourself as a featured creator</p>
                </div>
                {selectedContent?.type === 'profile' && <CheckCircle className="w-5 h-5 text-secondary ml-auto" />}
              </div>
            </Card>

            {/* Tracks */}
            {myContent.tracks.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Music className="w-4 h-4" /> Your Tracks
                </h3>
                <div className="space-y-2">
                  {myContent.tracks.slice(0, 10).map(track => (
                    <Card
                      key={track.track_id}
                      className={`p-3 cursor-pointer transition-all border-2 ${
                        selectedContent?.id === track.track_id ? 'border-secondary bg-secondary/10' : 'border-border hover:border-border'
                      }`}
                      onClick={() => setSelectedContent({ type: 'track', id: track.track_id, title: track.title })}
                      data-testid={`content-option-track-${track.track_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {track.cover_art_url ? (
                            <img src={track.cover_art_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Music className="w-5 h-5 text-orange-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{track.title}</p>
                          <p className="text-xs text-muted-foreground">{track.plays || 0} plays</p>
                        </div>
                        {selectedContent?.id === track.track_id && <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Videos */}
            {myContent.videos.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                  <Video className="w-4 h-4" /> Your Videos
                </h3>
                <div className="space-y-2">
                  {myContent.videos.slice(0, 10).map(video => (
                    <Card
                      key={video.video_id}
                      className={`p-3 cursor-pointer transition-all border-2 ${
                        selectedContent?.id === video.video_id ? 'border-secondary bg-secondary/10' : 'border-border hover:border-border'
                      }`}
                      onClick={() => setSelectedContent({ type: 'video', id: video.video_id, title: video.title })}
                      data-testid={`content-option-video-${video.video_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {video.thumbnail_url ? (
                            <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Video className="w-5 h-5 text-red-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{video.title}</p>
                          <p className="text-xs text-muted-foreground">{video.views || 0} views</p>
                        </div>
                        {selectedContent?.id === video.video_id && <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {myContent.tracks.length === 0 && myContent.videos.length === 0 && (
              <Card className="p-8 text-center border-dashed border-border">
                <Music className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">No tracks or videos yet. Upload content first to promote it!</p>
                <Button variant="outline" className="mt-3" onClick={() => navigate('/dashboard')}>
                  Upload Content
                </Button>
              </Card>
            )}

            {/* Checkout Button */}
            <div className="pt-4">
              <Button
                onClick={handlePurchase}
                disabled={!selectedContent || purchasing}
                className="w-full bg-secondary hover:bg-secondary/90 h-12 text-lg font-bold"
                data-testid="purchase-promotion-btn"
              >
                {purchasing ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <><DollarSign className="w-5 h-5 mr-2" /> Pay {pricing.find(p => p.slot_key === selectedSlot)?.price_display || ''} &amp; Go Live</>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Secure payment powered by Stripe. Your promotion goes live immediately after payment.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
